// What a marker looks like, on the map and in the legend and lists: a circle for a site, a square for a vehicle, in
// the status colour of the overview's cards, with a glyph per status, so that status is never told by colour alone.
// The classes are Tailwind's (the apps add this folder to their @source), and follow the light and dark themes.

import { cx } from '@cck/ui';
import type { AssetStatusCode } from './status.ts';

export type PointKind = 'site' | 'vehicle';

export const STATUS_FILL: Record<AssetStatusCode, string> = {
  alarm: 'bg-red-600 text-on-color', offline: 'bg-violet-500 text-on-color', warning: 'bg-amber-400 text-nuit',
  ok: 'bg-emerald-500 text-nuit', no_data: 'bg-slate-400 text-on-color',
};

/** SVG paths (24×24, stroked) of each status's glyph. */
const GLYPH: Record<AssetStatusCode, string> = {
  alarm: 'M12 5v9M12 18.5v.5',
  offline: 'M7 7l10 10M17 7L7 17',
  warning: 'M12 5l8 14H4z',
  ok: 'M5 12.5l4.5 4.5L19 7.5',
  no_data: 'M6 12h12',
};

export function glyphSvg(status: AssetStatusCode, size = 14): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true" fill="none" stroke="currentColor" `
    + `stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="${GLYPH[status]}"/></svg>`;
}

export const markerClass = (kind: PointKind, status: AssetStatusCode | null, selected = false, small = false) => cx(
  'relative flex items-center justify-center transition-transform',
  small ? 'h-5 w-5 border' : 'h-7 w-7 border-2 shadow-md',
  kind === 'site' ? 'rounded-full' : 'rounded-none',
  status ? `border-white ${STATUS_FILL[status]}` : 'border-slate-400 bg-slate-200',
  selected && 'z-10 scale-125 outline-2 outline-offset-2 outline-brand-500',
);

/** The marker as a React element, for the legend and the lists beside the map. */
export function MarkerIcon({ kind, status, small = false, bare = false }: { kind: PointKind; status: AssetStatusCode;
  small?: boolean; /** The shape only, without a status (the legend's site and vehicle). */ bare?: boolean }) {
  return (
    <span aria-hidden="true" className={cx(markerClass(kind, bare ? null : status, false, small), 'shrink-0')}
      dangerouslySetInnerHTML={{ __html: bare ? '' : glyphSvg(status, small ? 10 : 14) }} />
  );
}
